/**
 * @file SplashScene.ts
 * @role The splash/loading scene. Composes the loading view (title + progress bar) and
 *       a RetryOverlay under one root container, reflects asset-load progress, and
 *       surfaces recoverable load errors with a retry affordance.
 * @owns Scene lifecycle (enter/exit/resize), progress wiring, error display.
 * @reads LayoutContext, asset progress events.
 * @neverDoes Load assets itself (AssetService does). Decide game outcome.
 * @invariants 8, 12 (cleans up its subscription + resets overlay on exit), 14.
 * @see docs/UI_LAYOUT_GUIDE.md
 */
import { Container } from 'pixi.js';
import type { AssetBundleName } from '@core/assets/AssetBundleName';
import type { EventBus } from '@core/events/EventBus';
import type { UiConfig } from '@core/config/UiConfig';
import type { LayoutContext } from '@core/layout/LayoutContext';
import type { IScene } from '@scenes/IScene';
import { RetryOverlay } from '@ui/components/RetryOverlay';
import type { TextureResolver } from '@ui/components/BackgroundView';
import type { GameTitleView } from '@ui/hud/GameTitleView';
import { SplashSceneView } from './SplashSceneView';
import { ProgressPresenter } from './ProgressPresenter';

const BUNDLE_STATUS: Readonly<Record<string, string>> = {
  core: 'OPENING THE CASINO',
  ui: 'POLISHING THE TABLE',
  reels: 'PREPARING THE REELS',
};

export class SplashScene implements IScene {
  private readonly root = new Container();
  private readonly splashView: SplashSceneView;
  private readonly retryOverlay: RetryOverlay;
  private unsubscribe: (() => void) | undefined;
  private lastLayout: LayoutContext | undefined;
  private readonly progressByBundle = new Map<AssetBundleName, number>();
  // Display-only smoothing: events set the real target; update() eases the visible bar.
  private readonly progress = new ProgressPresenter();

  constructor(
    private readonly eventBus: EventBus,
    uiConfig: UiConfig,
    titleLockup: GameTitleView,
    private readonly bundleOrder: readonly AssetBundleName[],
    resolveTexture: TextureResolver = () => undefined,
    loadingBackgroundId = 'background_loading',
    reducedMotion = false,
  ) {
    for (const bundleName of bundleOrder) this.progressByBundle.set(bundleName, 0);
    this.splashView = new SplashSceneView(
      resolveTexture,
      loadingBackgroundId,
      uiConfig,
      titleLockup,
    );
    this.retryOverlay = new RetryOverlay(
      uiConfig.modal,
      uiConfig.theme,
      resolveTexture,
      reducedMotion,
    );
    this.retryOverlay.onActivated(() => this.eventBus.emit('ui:activated'));
    this.root.addChild(this.splashView, this.retryOverlay);
  }

  get view(): Container {
    return this.root;
  }

  enter(): void {
    this.unsubscribe = this.eventBus.on('assets:bundle_progress', ({ bundleName, progress01 }) => {
      const trackedBundle = bundleName as AssetBundleName;
      if (!this.progressByBundle.has(trackedBundle)) return;
      this.progressByBundle.set(trackedBundle, Math.max(0, Math.min(1, progress01)));
      const overallProgress =
        this.bundleOrder.length > 0
          ? [...this.progressByBundle.values()].reduce((total, value) => total + value, 0) /
            this.bundleOrder.length
          : 1;
      this.progress.setTarget(overallProgress);
      this.splashView.setStatus(BUNDLE_STATUS[bundleName] ?? 'PREPARING THE CASINO');
    });
  }

  update(deltaMs: number): void {
    this.splashView.setProgress(this.progress.update(deltaMs));
  }

  exit(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.retryOverlay.hide();
    this.root.destroy({ children: true });
  }

  resize(layout: LayoutContext): void {
    this.lastLayout = layout;
    this.splashView.applyLayout(layout);
    this.retryOverlay.applyLayout(layout);
  }

  /** Show a recoverable error with a Retry button. */
  showError(message: string, onRetry: () => void): void {
    this.retryOverlay.show(message, onRetry);
    if (this.lastLayout) {
      this.retryOverlay.applyLayout(this.lastLayout);
    }
  }

  clearError(): void {
    this.retryOverlay.hide();
  }
}
